// RealtimeInvalidationProvider — глобальный socket → TanStack Query invalidate.
// Документирован как useSocketInvalidation() в frontend-realtime.md §1.
//
// Монтируется в __root рядом с NotificationToastListener. Detail-роуты могут
// оставлять локальные handlers (optimistic / navigate / toast), но списки
// обновляются отсюда.

import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import {
	REALTIME_INVALIDATION_MAP,
	reconnectInvalidationKeys,
} from "@/lib/realtime/invalidation-map";
import { useSocket } from "./socket-provider";

/**
 * Окно коалесинга инвалидации. Достаточно, чтобы схлопнуть пачку терминальных
 * событий одной генерации, и незаметно для человека.
 */
const COALESCE_MS = 40;

/** Side-effect listener (как NotificationToastListener). Монтировать в __root. */
export function RealtimeInvalidationProvider(): null {
	const socket = useSocket();
	const qc = useQueryClient();
	const hadConnectedRef = useRef(false);

	useEffect(() => {
		if (!socket) return;

		// Коалесинг волны инвалидации.
		//
		// Терминальные события часто прилетают пачкой: сервер намеренно эмитит
		// `course:topic_kit_ready` И `study-plan:subsection-materials:ready` (двойной
		// эмит под гейтом course-workspace-v2-wireup), а на `quiz:ready` подписано
		// под десяток поверхностей. Без коалесинга один и тот же ключ
		// инвалидируется столько раз, сколько событий, а `invalidateQueries` по
		// умолчанию идёт с `cancelRefetch: true` — то есть отменяет и перезапускает
		// уже летящий refetch. На слабой сети это лишние round-trip'ы.
		//
		// Снимать подписки нельзя (правило «realtime во ВСЕ поверхности»), поэтому
		// режем не источники, а повторы: набор ключей остаётся тем же, каждый
		// инвалидируется один раз за окно.
		const queuedKeys = new Map<string, unknown[]>();
		let flushTimer: ReturnType<typeof setTimeout> | undefined;

		const flush = () => {
			flushTimer = undefined;
			const keys = [...queuedKeys.values()];
			queuedKeys.clear();
			for (const key of keys) void qc.invalidateQueries({ queryKey: key });
		};

		const enqueue = (key: unknown[]) => {
			queuedKeys.set(JSON.stringify(key), key);
			if (flushTimer === undefined) flushTimer = setTimeout(flush, COALESCE_MS);
		};

		const invalidateKey = (key: readonly unknown[]) => {
			enqueue([...key]);
			// tRPC react-query: [['studyPlan','get'], {input,type}] — префикс [['studyPlan']]
			const head = key[0];
			if (typeof head === "string") {
				enqueue([[head]]);
			}
		};

		const handlers: Array<{ event: string; fn: (...args: unknown[]) => void }> = [];
		for (const entry of REALTIME_INVALIDATION_MAP) {
			const keys = entry.keys;
			const fn = (...args: unknown[]) => {
				// Snappy title patch before refetch (open chat header / sessions list).
				if (entry.event === "tutor:session:title_changed") {
					const payload = args[0] as { sessionId?: string; title?: string } | undefined;
					const sessionId = payload?.sessionId;
					const nextTitle = typeof payload?.title === "string" ? payload.title : null;
					if (sessionId && nextTitle !== null) {
						qc.setQueryData<{ items: Array<{ id: string; title: string }> }>(
							["tutor", "sessions"],
							(old) => {
								if (!old?.items) return old;
								return {
									...old,
									items: old.items.map((s) =>
										s.id === sessionId ? { ...s, title: nextTitle } : s,
									),
								};
							},
						);
					}
				}
				for (const key of keys) invalidateKey(key);
			};
			handlers.push({ event: entry.event, fn });
			socket.on(entry.event as never, fn as never);
		}

		const onConnect = () => {
			if (hadConnectedRef.current) {
				for (const key of reconnectInvalidationKeys()) invalidateKey(key);
			}
			hadConnectedRef.current = true;
		};
		socket.on("connect", onConnect);
		if (socket.connected) onConnect();

		return () => {
			for (const { event, fn } of handlers) {
				socket.off(event as never, fn as never);
			}
			socket.off("connect", onConnect);
			// Хвост окна коалесинга не должен инвалидировать после размонтирования.
			if (flushTimer !== undefined) clearTimeout(flushTimer);
		};
	}, [socket, qc]);

	return null;
}

/** Alias под имя из docs. */
export const useSocketInvalidation = RealtimeInvalidationProvider;
